// pages/PaymentsPage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PaymentsTableMain from '../components/PaymentsTableMain';
import FlashMessage from '../components/FlashMessage'; // Import the new FlashMessage component
import { PageContainer, ContentContainer, Header, Label, DateInputField } from '../styles/ComponentStyles'
import apiService from '../services/apiService';

const PaymentsPage = ({ isSidebarOpen }) => {
  // const [totalItems, setTotalItems] = useState([]);
  // const [totalPages, setTotalPages] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageLimit, setCurrentPageLimit] = useState(10);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');

  const refreshData = () => {
    setRefreshKey(prevKey => prevKey + 1);
  };

  const handleFlashMessage = (message) => {
    setFlashMessage(message);
    setTimeout(() => {
      setFlashMessage(''); // Clear the message after a delay
    }, 3000);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageLimitChange = (newLimit) => {
    setCurrentPageLimit(newLimit);
  };

  const handlePeriodFilter = (period) => {
    setSelectedPeriod(period);
    setShowCustomDateRange(false);
    setCurrentPage(1); // Reset to first page
  };

  const handleCustomDateFilter = () => {
    if (customStartDate && customEndDate) {
      setSelectedPeriod('custom');
      setShowCustomDateRange(false);
      setCurrentPage(1); // Reset to first page
    }
  };

  const handleClearFilters = () => {
    setSelectedPeriod(null);
    setCustomStartDate('');
    setCustomEndDate('');
    setShowCustomDateRange(false);
    setCurrentPage(1);
  };

  // This list knows a payment id and nothing about which bill holds it, so the
  // delete has to be the payment-scoped one. It used to call
  // deletePayment(paymentId) against a (bill_id, payment_id) signature — the URL
  // came out as /bills/{paymentId}/payments/undefined — and then read `.data` off
  // a fetch Response, so it threw every time and flashed "undefined".
  const handleDelete = async (paymentId) => {
    try {
      const result = await apiService.deletePaymentById(paymentId);
      handleFlashMessage((result?.message || 'Payment deleted.') + ' Refreshing the page.');
      setRefreshKey(prevKey => prevKey + 1);
    } catch (error) {
      console.error(error);
      handleFlashMessage(error?.message || 'Failed to delete the payment.');
    }
  };

  return (
    <PageContainer>
      <ContentContainer isSidebarOpen={isSidebarOpen}>
        <Header>
          <h1>Manage Payments</h1>
        </Header>
        <p style={{ marginTop: 0, marginBottom: '15px' }}>
          Payments are recorded on a <Link to="/bills">Bill</Link> or a <Link to="/subscriptions">Subscription</Link>.
        </p>
        {flashMessage && <FlashMessage message={flashMessage} />} {/* Use the FlashMessage component here */}

        {/* Filter Controls */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>Filter Payments:</div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={() => handlePeriodFilter('today')}
              style={{
                padding: '8px 15px',
                backgroundColor: selectedPeriod === 'today' ? '#4CAF50' : '#ddd',
                color: selectedPeriod === 'today' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: selectedPeriod === 'today' ? 'bold' : 'normal'
              }}
            >
              Today
            </button>
            <button
              onClick={() => handlePeriodFilter('week')}
              style={{
                padding: '8px 15px',
                backgroundColor: selectedPeriod === 'week' ? '#4CAF50' : '#ddd',
                color: selectedPeriod === 'week' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: selectedPeriod === 'week' ? 'bold' : 'normal'
              }}
            >
              This Week
            </button>
            <button
              onClick={() => handlePeriodFilter('month')}
              style={{
                padding: '8px 15px',
                backgroundColor: selectedPeriod === 'month' ? '#4CAF50' : '#ddd',
                color: selectedPeriod === 'month' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: selectedPeriod === 'month' ? 'bold' : 'normal'
              }}
            >
              This Month
            </button>
            <button
              onClick={() => handlePeriodFilter('quarter')}
              style={{
                padding: '8px 15px',
                backgroundColor: selectedPeriod === 'quarter' ? '#4CAF50' : '#ddd',
                color: selectedPeriod === 'quarter' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: selectedPeriod === 'quarter' ? 'bold' : 'normal'
              }}
            >
              This Quarter
            </button>
            <button
              onClick={() => setShowCustomDateRange(!showCustomDateRange)}
              style={{
                padding: '8px 15px',
                backgroundColor: selectedPeriod === 'custom' ? '#4CAF50' : '#ddd',
                color: selectedPeriod === 'custom' ? 'white' : 'black',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: selectedPeriod === 'custom' ? 'bold' : 'normal'
              }}
            >
              Custom Range
            </button>
            {selectedPeriod && (
              <button
                onClick={handleClearFilters}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#f44336',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>

          {/* Custom Date Range Inputs */}
          {showCustomDateRange && (
            <div style={{ marginTop: '15px', display: 'flex', gap: '10px', alignItems: 'flex-end' }}>
              <div>
                <Label htmlFor="customStartDate">Start Date</Label>
                <DateInputField
                  id="customStartDate"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="customEndDate">End Date</Label>
                <DateInputField
                  id="customEndDate"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
              <button
                onClick={handleCustomDateFilter}
                style={{
                  padding: '8px 15px',
                  backgroundColor: '#2196F3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Apply
              </button>
            </div>
          )}
        </div>

        <PaymentsTableMain
          handleFlashMessage={handleFlashMessage}
          refreshKey={refreshKey}
          setRefreshKey={setRefreshKey}
          refreshData={refreshData}
          handleDelete={handleDelete}
          currentPage={currentPage}
          currentPageLimit={currentPageLimit}
          onPageChange={handlePageChange}
          onPageLimitChange={handlePageLimitChange}
          period={selectedPeriod}
          startDate={customStartDate}
          endDate={customEndDate}
        />
      </ContentContainer>
    </PageContainer>
  );
};

export default PaymentsPage;
